import { Link, Text } from "@react-pdf/renderer"
import React from "react"

// Shared helper to process inline markdown patterns into @react-pdf nodes
// Supported:
// - **bold**
// - *italic*
// - [label](https://example.com)
// - `code` (simple styling)
// - <sup>text</sup> and <sub>text</sub> (approximate with smaller font and offset)
export const processInlineMarkdown = (text: string): React.ReactNode => {
  if (!text) return text

  // Normalize accidental backslash-escaped asterisk groups inside parentheses
  // 1) Only backslashes: (\) -> (*), (\\) -> (**), (\\\) -> (***)
  // 2) Mixed slashes and asterisks: (\*), (\\**), (\\\***) -> strip slashes to keep literal asterisks
  // Some PDF text flows may escape these; convert them back before parsing.
  text = text
    .replace(/\((\\+)\)/g, (_m, slashes: string) => `(${"*".repeat(slashes.length)})`)
    .replace(/\(([\\*]+)\)/g, (_m, inner: string) => {
      const starsOnly = inner.replace(/\\/g, "")
      return starsOnly.length > 0 ? `(${starsOnly})` : `(${"*".repeat(inner.length)})`
    })

  // First, extract links so we can preserve them and process their label content
  const linkRegex = /\[([^\]]+)\]\(([^\s)]+)\)/g

  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let linkMatch: RegExpExecArray | null

  while ((linkMatch = linkRegex.exec(text)) !== null) {
    const [full, label, url] = linkMatch

    // Add text before this link, processed for bold/italic/code
    if (linkMatch.index > lastIndex) {
      const before = text.substring(lastIndex, linkMatch.index)
      if (before) nodes.push(...toInlineStyledSegments(before))
    }

    // Add the link itself, allowing bold/italic/code inside the label
    nodes.push(
      <Link
        key={`lnk-${linkMatch.index}`}
        src={url}
        style={{ textDecoration: "underline", color: "#1a0dab" }}
      >
        {toInlineStyledSegments(label)}
      </Link>
    )

    lastIndex = linkMatch.index + full.length
  }

  // Remainder after last link
  if (lastIndex < text.length) {
    const rest = text.substring(lastIndex)
    if (rest) nodes.push(...toInlineStyledSegments(rest))
  }

  return nodes.length > 0 ? nodes : text
}

// Convert a plain text segment into an array of nodes with bold/italic/code applied.
const toInlineStyledSegments = (segment: string): React.ReactNode[] => {
  if (!segment) return []

  // Preserve literal asterisk groups inside parentheses e.g. (*), (**), (***).
  // Split so these pass through untouched and only other parts are parsed.
  const literalGroupPattern = /(\(\*+\))/g
  const parts = segment.split(literalGroupPattern)
  if (parts.length > 1) {
    const out: React.ReactNode[] = []
    for (const p of parts) {
      if (!p) continue
      if (/^\(\*+\)$/.test(p)) {
        out.push(p)
      } else {
        out.push(...styleCore(p))
      }
    }
    return out
  }

  return styleCore(segment)
}

// Core styling (bold, italic, code) applied to a plain segment (no literal groups)
const styleCore = (segment: string): React.ReactNode[] => {
  if (!segment) return []

  // Build a combined list of matches to preserve order and avoid overlaps
  type Match = {
    start: number
    end: number
    type: "bold" | "italic" | "code" | "sup" | "sub"
    content: string
  }

  const boldRegex = /\*\*(.+?)\*\*/g
  const italicRegex = /(?<!\*)\*(?!\*)(.+?)\*(?!\*)/g
  const codeRegex = /`([^`]+)`/g
  const supRegex = /<sup>([\s\S]*?)<\/sup>/g
  const subRegex = /<sub>([\s\S]*?)<\/sub>/g

  const matches: Match[] = []
  let m: RegExpExecArray | null

  while ((m = boldRegex.exec(segment)) !== null) {
    matches.push({ start: m.index, end: m.index + m[0].length, type: "bold", content: m[1] })
  }

  while ((m = codeRegex.exec(segment)) !== null) {
    // Avoid overlaps with existing bold ranges
    const startIndex = m.index
    const endIndex = m.index + m[0].length
    if (!matches.some((x) => startIndex > x.start && startIndex < x.end)) {
      matches.push({ start: startIndex, end: endIndex, type: "code", content: m[1] })
    }
  }

  while ((m = supRegex.exec(segment)) !== null) {
    const startIndex = m.index
    const endIndex = m.index + m[0].length
    if (!matches.some((x) => startIndex > x.start && startIndex < x.end)) {
      matches.push({ start: startIndex, end: endIndex, type: "sup", content: m[1] })
    }
  }

  while ((m = subRegex.exec(segment)) !== null) {
    const startIndex = m.index
    const endIndex = m.index + m[0].length
    if (!matches.some((x) => startIndex > x.start && startIndex < x.end)) {
      matches.push({ start: startIndex, end: endIndex, type: "sub", content: m[1] })
    }
  }

  while ((m = italicRegex.exec(segment)) !== null) {
    // Avoid overlaps with bold and code ranges
    const startIndex = m.index
    const endIndex = m.index + m[0].length
    if (!matches.some((x) => startIndex > x.start && startIndex < x.end)) {
      matches.push({ start: startIndex, end: endIndex, type: "italic", content: m[1] })
    }
  }

  // Sort by start position
  matches.sort((a, b) => a.start - b.start)

  // Build nodes from text and matches
  const out: React.ReactNode[] = []
  let cursor = 0

  matches.forEach((mtch, i) => {
    // Add plain text before this match
    if (mtch.start > cursor) {
      const plain = segment.substring(cursor, mtch.start)
      if (plain) out.push(plain)
    }

    // Add formatted content
    if (mtch.type === "bold") {
      out.push(
        <Text key={`b-${i}`} style={{ fontWeight: "bold" }}>
          {mtch.content}
        </Text>
      )
    } else if (mtch.type === "italic") {
      out.push(
        <Text key={`i-${i}`} style={{ fontStyle: "italic" }}>
          {mtch.content}
        </Text>
      )
    } else if (mtch.type === "code") {
      out.push(
        <Text key={`c-${i}`} style={{ fontFamily: "Courier", backgroundColor: "#f1f3f5" }}>
          {mtch.content}
        </Text>
      )
    } else if (mtch.type === "sup") {
      out.push(
        <Text key={`s-${i}`} style={{ fontSize: 7, marginTop: -3 }}>
          {mtch.content}
        </Text>
      )
    } else if (mtch.type === "sub") {
      out.push(
        <Text key={`u-${i}`} style={{ fontSize: 7, marginBottom: -2 }}>
          {mtch.content}
        </Text>
      )
    }

    cursor = mtch.end
  })

  // Tail
  if (cursor < segment.length) {
    const tail = segment.substring(cursor)
    if (tail) out.push(tail)
  }

  return out
}

export default processInlineMarkdown
